import { useEffect, useId } from 'react';
import type { ReactNode } from 'react';
import Modal from 'react-bootstrap/Modal';

import { Button } from '../buttons';
import type { ButtonProps } from '../buttons/utils';
import DraggableModalDialog from '../../containers/DraggableModalDialog';
import { setZIndexForModalForms } from '../../../../lib/functions';

/** Пропсы модалки подтверждения ConfirmModal. */
export interface ConfirmModalProps {
  show: boolean;
  /** заголовок — он же титулбар-ручка (DraggableModalDialog) */
  title: ReactNode;
  /** текст/контент вопроса */
  body?: ReactNode;
  /** кнопка подтверждения (по умолчанию «Подтвердить») */
  confirmText?: string;
  /** variant кнопки подтверждения; danger — для разрушительных действий */
  confirmVariant?: ButtonProps['variant'];
  /** кнопка отмены (по умолчанию «Отмена») */
  cancelText?: string;
  size?: 'sm' | 'lg' | 'xl';
  /** «Да» */
  onConfirm(): void;
  /** «Нет»: кнопка, крестик, Escape, клик по фону */
  onCancel(): void;
  /** dialogClassName модалки */
  dialogClassName?: string;
}

/**
 * Модалка подтверждения «вопрос → Отмена / Подтвердить» без Formik — для
 * простых да/нет сценариев (несохранённые данные, сброс, удаление). Построена
 * на перетаскиваемом окне (DraggableModalDialog): заголовок живёт в
 * титулбаре-ручке, на тач-раскладке деградирует в шторку со свайпом.
 * Пары «да/нет + ввод» — PromptModal/PromptModalWithSMS, формы — FormModal.
 */
const ConfirmModal = (props: ConfirmModalProps) => {
  const {
    show,
    title,
    body,
    confirmText = 'Подтвердить',
    confirmVariant = 'primary',
    cancelText = 'Отмена',
    size = 'sm',
    onConfirm,
    onCancel,
    dialogClassName,
  } = props;

  // id заголовка в полоске-ручке: сохраняет aria-labelledby модалки
  // (штатный Modal.Header скрыт — его заменяет ручка)
  const titleId = useId();

  // Подстраиваем z-index под общий стек оверлеев проекта (см. FormModal/Drawer):
  // подтверждение обычно открывается поверх другой модалки
  useEffect(() => {
    if (show) {
      setZIndexForModalForms();
    }
  }, [ show ]);

  return (
    <Modal
      show={ show }
      size={ size }
      centered
      aria-labelledby={ titleId }
      dialogClassName={ dialogClassName }
      onHide={ onCancel }
      dialogAs={ DraggableModalDialog }
      { ...({
        // кастомные пропсы DraggableModalDialog (handleTitle/onHandleClose) не входят
        // в ModalProps react-bootstrap — прокидываются в dialogAs, как в FormModal
        handleTitle: title,
        handleTitleId: titleId,
        onHandleClose: onCancel,
      } as any) }
    >
      <Modal.Body>{ body }</Modal.Body>
      <Modal.Footer>
        <Button variant='outline-secondary' text={ cancelText } onClick={ onCancel } />
        <Button variant={ confirmVariant } text={ confirmText } onClick={ onConfirm } />
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmModal;
